import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getApiBase } from "../apiBase";
import { isCvinExpansion } from "../projectGridColumns";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import { buildExpansionSegmentGroups } from "./expansionGroupUtils";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import { viewerCanSubmitDailyProgressReports } from "./expansionInspectorAccess";
import ExpansionDailyProgressForm from "./ExpansionDailyProgressForm";
import {
  dismissExpansionDailyProgress,
  expansionDailyProgressDismissKey,
  isExpansionDailyProgressDismissed,
  submitExpansionDailyProgress,
} from "./expansionDailyProgressApi";
import "./workflowSchedule.css";

/** Post-login modal for Inspectors and Construction Leads to report daily progress. */
export default function InspectorDailyFootageModal({ apiUrl, getAuthHeaders, enabled }) {
  const viewerEmail = getViewerEmailFromStorage();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [segmentKey, setSegmentKey] = useState("");
  const [projectId, setProjectId] = useState("");
  const [conduitFootage, setConduitFootage] = useState("");
  const [fiberFootage, setFiberFootage] = useState("");
  const [proofingFootage, setProofingFootage] = useState("");
  const [vaults, setVaults] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const segmentGroups = useMemo(() => buildExpansionSegmentGroups(projects), [projects]);

  const selectedGroup = useMemo(
    () => segmentGroups.find((g) => g.key === segmentKey) || null,
    [segmentGroups, segmentKey]
  );

  const subsegmentOptions = selectedGroup?.children ?? [];
  const dismissKey = expansionDailyProgressDismissKey("global", viewerEmail);
  const canPrompt = viewerCanSubmitDailyProgressReports(viewerEmail);

  useEffect(() => {
    if (!enabled || !canPrompt) return;
    if (isExpansionDailyProgressDismissed(dismissKey)) return;
    setOpen(true);
  }, [canPrompt, dismissKey, enabled]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingProjects(true);
    (async () => {
      try {
        const base = apiUrl || getApiBase();
        const { data } = await axios.get(`${base}/projects`, { headers: getAuthHeaders() });
        if (cancelled) return;
        const rows = Array.isArray(data) ? data.filter(isCvinExpansion) : [];
        setProjects(rows);
        const groups = buildExpansionSegmentGroups(rows);
        if (groups.length) {
          setSegmentKey(groups[0].key);
          setProjectId(String(groups[0].children[0]?.id ?? ""));
        }
      } catch {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, getAuthHeaders, open]);

  useEffect(() => {
    if (!selectedGroup?.children?.length) {
      setProjectId("");
      return;
    }
    if (!selectedGroup.children.some((c) => String(c.id) === String(projectId))) {
      setProjectId(String(selectedGroup.children[0].id));
    }
  }, [projectId, selectedGroup]);

  const dismissForToday = useCallback(() => {
    dismissExpansionDailyProgress(dismissKey);
    setOpen(false);
  }, [dismissKey]);

  const submit = useCallback(async () => {
    const row = projects.find((p) => String(p.id) === String(projectId));
    if (!row) {
      setError("Select a subsegment.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await submitExpansionDailyProgress({
        projectRow: row,
        report: { conduitFootage, fiberFootage, proofingFootage, vaults, notes },
        apiUrl,
        getAuthHeaders,
      });
      dismissForToday();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not save report.");
    } finally {
      setBusy(false);
    }
  }, [
    apiUrl,
    conduitFootage,
    dismissForToday,
    fiberFootage,
    getAuthHeaders,
    notes,
    projectId,
    projects,
    proofingFootage,
    vaults,
  ]);

  if (!open || !canPrompt) return null;

  return (
    <div className="schedule-calendar-modal-backdrop schedule-calendar-modal-backdrop--sheet" role="presentation">
      <div
        className="schedule-calendar-modal schedule-calendar-modal--sheet inspector-daily-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inspector-footage-title"
      >
        <h2 id="inspector-footage-title" className="schedule-calendar-modal-title">
          Daily contractor progress
        </h2>
        <p className="schedule-calendar-modal-meta">
          Report conduit footage, fiber footage, proofing completed, vaults placed, and notes for
          today. Footage adds to the subsegment installed totals.
        </p>

        {loadingProjects ? <p className="project-detail-muted">Loading segments…</p> : null}

        <div className="schedule-calendar-modal-forms">
          <label className="phase-schedule-field">
            <span className="phase-schedule-field-label">Segment</span>
            <select
              className="commercial-field-input"
              value={segmentKey}
              disabled={busy || loadingProjects}
              onChange={(e) => setSegmentKey(e.target.value)}
            >
              {segmentGroups.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.title}
                </option>
              ))}
            </select>
          </label>

          <label className="phase-schedule-field">
            <span className="phase-schedule-field-label">Subsegment</span>
            <select
              className="commercial-field-input"
              value={projectId}
              disabled={busy || !subsegmentOptions.length}
              onChange={(e) => setProjectId(e.target.value)}
            >
              {subsegmentOptions.map((row) => {
                const sub = readSubsegmentSummaryFromAcf(row.acf_groups_json);
                const label = [row.work_order, sub?.project_name ?? row.name].filter(Boolean).join(" · ");
                return (
                  <option key={row.id} value={row.id}>
                    {label || row.id}
                  </option>
                );
              })}
            </select>
          </label>

          <ExpansionDailyProgressForm
            idPrefix="inspector-daily"
            conduitFootage={conduitFootage}
            fiberFootage={fiberFootage}
            proofingFootage={proofingFootage}
            vaults={vaults}
            notes={notes}
            disabled={busy}
            onConduitFootageChange={setConduitFootage}
            onFiberFootageChange={setFiberFootage}
            onProofingFootageChange={setProofingFootage}
            onVaultsChange={setVaults}
            onNotesChange={setNotes}
          />
        </div>

        {error ? (
          <p className="project-detail-error" style={{ fontSize: 13 }}>
            {error}
          </p>
        ) : null}

        <div className="schedule-calendar-modal-actions">
          <button type="button" className="email-triggers-button" disabled={busy} onClick={dismissForToday}>
            Remind me later
          </button>
          <button
            type="button"
            className="email-triggers-button email-triggers-button-primary"
            disabled={busy || !projectId}
            onClick={() => void submit()}
          >
            {busy ? "Saving…" : "Submit report"}
          </button>
        </div>
      </div>
    </div>
  );
}
